import {chunk} from '@benchling/js-utils';
import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import * as FileConstants from 'folder-items/FileConstants';
import {
  ComplexPolymerSchemaConfigGraphQL,
  generateEntityAssociationsFromTags,
  getNextPolymerRefFromTag,
  updateEntityAssociationWithPolymerRef,
} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import BioentityLinkInput from 'schemas/tags/BioentityLinkInput';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_SEMIDARK} from 'ui-library/Colors';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {ElementKeyboardEvent} from 'util/Types';

import {
  ComplexPolymerCreateInputSection,
  CreateComplexPolymerSchemaTag,
  CreateComplexPolymerTargetFile,
  getComplexPolymerCreateSections,
} from './CreateComplexPolymerUtil';

interface ComplexPolymerInputsProps {
  name: string;
  setName: (name: string) => void;
  folderId?: string | null;
  setFolderId: (folderId: string | null | undefined) => void;
}

interface ComplexPolymerNameAndFolderInputsProps extends ComplexPolymerInputsProps {}

export function ComplexPolymerNameAndFolderInputs({
  name,
  setName,
  folderId,
  setFolderId,
}: ComplexPolymerNameAndFolderInputsProps): JSX.Element {
  return (
    <>
      <Row>
        <Col sm={6}>
          <FormGroup>
            <FieldLabel label="Entity name" isRequired={true} />
            <input
              autoFocus={true}
              type="text"
              value={name}
              placeholder="Name"
              onChange={(e) => {
                setName(e.target.value);
              }}
              {...getTestProps({component: 'CreateComplexPolymerModal', element: 'input', key: 'entityName'})}
              data-amp-safe={false}
            />
          </FormGroup>
        </Col>
        <Col sm={6}>
          <FormGroup>
            <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
            <FolderSelector
              enableRedesign={true}
              showInventory={true}
              value={folderId}
              onChange={(e) => {
                setFolderId(e.target.value);
              }}
              unsetOnFetchFail={true}
              {...getTestProps({component: 'FolderSelector', element: 'root'})}
            />
          </FormGroup>
        </Col>
      </Row>
    </>
  );
}

interface QueryingComplexPolymerFieldInputsProps {
  complexPolymerConfig: ComplexPolymerSchemaConfigGraphQL;
  complexPolymerTags: Array<CreateComplexPolymerSchemaTag>;
  setComplexPolymerTags: (complexPolymerTags: Array<CreateComplexPolymerSchemaTag>) => void;
}

export function QueryingComplexPolymerFieldInputs({
  complexPolymerConfig,
  complexPolymerTags,
  setComplexPolymerTags,
}: QueryingComplexPolymerFieldInputsProps): JSX.Element {
  const entityAssociations = generateEntityAssociationsFromTags(complexPolymerTags);

  function getComplexPolymerTargetFiles(
    targetFiles: Array<FileConstants.TargetFile>,
    schemaFieldId: string
  ): Array<CreateComplexPolymerTargetFile> {
    const complexPolymerTargetFiles: Array<CreateComplexPolymerTargetFile> = targetFiles.map((file) => {
      if (file.hasOwnProperty('polymerRef')) {
        return file as CreateComplexPolymerTargetFile;
      }

      const polymerRef = getNextPolymerRefFromTag(
        complexPolymerConfig,
        schemaFieldId,
        file,
        entityAssociations
      );

      updateEntityAssociationWithPolymerRef(file.id, polymerRef, entityAssociations);
      return {...file, polymerRef};
    });

    return complexPolymerTargetFiles;
  }

  return (
    <ComplexPolymerFieldInputs
      complexPolymerTags={complexPolymerTags}
      complexPolymerConfig={complexPolymerConfig}
      // TODO (BNCH-88990): Make onKeyDown match regular schema tag behavior.
      onKeyDown={(e) => {}}
      onChange={(fieldId, tagValue, value) => {
        setComplexPolymerTags(
          complexPolymerTags.map((tag) =>
            tag.tagSchemaField.apiId === fieldId
              ? {
                  ...tag,
                  targetFiles: getComplexPolymerTargetFiles(tagValue.targetFiles ?? [], fieldId),
                  value,
                }
              : tag
          )
        );
      }}
    />
  );
}

interface ComplexPolymerFieldInputsProps {
  complexPolymerTags: Array<CreateComplexPolymerSchemaTag>;
  complexPolymerConfig: ComplexPolymerSchemaConfigGraphQL;
  onChange: (fieldId: string, tagValue: Partial<FileConstants.SchemaTag>, value?: string) => void;
  onKeyDown: (e: ElementKeyboardEvent) => void;
}

export function ComplexPolymerFieldInputs({
  complexPolymerTags,
  complexPolymerConfig,
  onChange,
  onKeyDown,
}: ComplexPolymerFieldInputsProps): JSX.Element {
  const complexPolymerInputSections: Array<ComplexPolymerCreateInputSection> =
    getComplexPolymerCreateSections(complexPolymerConfig, complexPolymerTags);

  return (
    <div>
      {complexPolymerInputSections.map((section, idx) => (
        <div key={idx}>
          <FieldLabel className={css(styles.sectionHeader)} label={section.sectionHeader} />
          {Array.from(chunk(section.inputTags, 2)).map((chunk, i) => {
            const tag1 = chunk[0];
            const tag2 = chunk[1] ?? null;
            return (
              <Row key={i}>
                <Col sm={6}>
                  <ComplexPolymerFieldInput
                    tag={tag1}
                    isRequired={tag1.tagSchemaField.isRequired}
                    tagSchemaField={tag1.tagSchemaField}
                    onChange={(tagValue, value) => {
                      onChange(tag1.tagSchemaField.apiId, tagValue, value);
                    }}
                    onKeyDown={onKeyDown}
                  />
                </Col>
                {tag2 != null && (
                  <Col sm={6}>
                    <ComplexPolymerFieldInput
                      tag={tag2}
                      isRequired={tag2.tagSchemaField.isRequired}
                      tagSchemaField={tag2.tagSchemaField}
                      onChange={(tagValue, value) => {
                        onChange(tag2.tagSchemaField.apiId, tagValue, value);
                      }}
                      onKeyDown={onKeyDown}
                    />
                  </Col>
                )}
              </Row>
            );
          })}
          {idx < complexPolymerInputSections.length - 1 && <hr className={css(styles.sectionDivider)} />}
        </div>
      ))}
    </div>
  );
}

interface ComplexPolymerFieldInputProps {
  tag: CreateComplexPolymerSchemaTag;
  tagSchemaField: SchemaField;
  isRequired: boolean;
  onChange: (tagValue: Partial<FileConstants.SchemaTag>, value?: string) => void;
  onKeyDown: (e: ElementKeyboardEvent) => void;
}

function ComplexPolymerFieldInput(props: ComplexPolymerFieldInputProps): JSX.Element {
  return (
    <div
      {...getTestProps({
        component: 'CreateComplexPolymerModal',
        element: 'input',
        key: props.tagSchemaField.name,
      })}
      data-amp-safe={false}
    >
      <FieldLabel label={props.tagSchemaField.name} isRequired={props.isRequired} />
      <BioentityLinkInput
        onChange={props.onChange}
        onKeyDown={props.onKeyDown}
        chipSize={ChipSize.SMALL}
        placeholder={`Select ${props.tagSchemaField.name}`}
        summarize={true}
        tag={props.tag}
        autoFocus={false}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  sectionHeader: {
    color: GRAY_SEMIDARK,
    marginBottom: '15px',
    fontSize: '12px',
  },
  sectionDivider: {
    marginBottom: '11px',
    marginTop: '15px',
  },
});
